import { useState, useMemo } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import {
  CheckCircle,
  Close,
  HelpOutline,
  InfoOutlined,
  Refresh,
  Warning,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import clsx from 'clsx';
import { stringUtilities } from 'utils';
import { useStore } from 'hooks';

import TroubleshootModal from '../TroubleshootModal';

const SystemOfflineBanner = ({
  closeBanner,
  refreshTimeLeft,
  handleRefresh,
  isRefreshing,
  onlineCountdown,
  isBackOnline,
  handleDetailsClick,
}) => {
  const { t } = useTranslation();
  const { hardwareStatusStore } = useStore();

  const [troubleshootingModalOpen, setTroubleshootingModalOpen] = useState(false);

  const handleTroubleshootingClick = () => {
    setTroubleshootingModalOpen(true);
  };

  const status = useMemo(() => {
    if (hardwareStatusStore.isRestored) return t('shared.SystemOffline.systemConnectivityRestored');
    if (hardwareStatusStore.isFullyOffline) return t('shared.SystemOffline.systemOffline');
    return t('shared.SystemOffline.partialSystemOffline');
  }, [t, hardwareStatusStore.isRestored, hardwareStatusStore.isFullyOffline]);

  return (
    <>
      <StyledRootDiv className={clsx({ restored: hardwareStatusStore.isRestored })}>
        <div className={'menuOptions'}>
          <span className={'menuTitle'}>
            {hardwareStatusStore.isRestored ? (
              <CheckCircle />
            ) : (
              <Warning sx={{ width: '24px' }} htmlColor={'#000'} />
            )}
            {status}
          </span>
          {hardwareStatusStore.isRestored ? (
            <span className={'menuOption online'}>
              <span className={clsx('timer online', { active: isBackOnline })}>
                {stringUtilities.formatTimer(onlineCountdown)}
              </span>
              <span className={'menuText'} onClick={closeBanner}>
                <Close />
              </span>
            </span>
          ) : (
            <div className={'menuButtons'}>
              <span className={'menuOption'} onClick={handleTroubleshootingClick}>
                <HelpOutline htmlColor={'#000'} />
              </span>
              <span className={'menuOption'} onClick={handleDetailsClick}>
                <InfoOutlined htmlColor={'#000'} />
              </span>
              {isRefreshing ? (
                <span className={clsx('menuOption timer refresh', { active: isRefreshing })}>
                  {stringUtilities.formatTimer(refreshTimeLeft)}
                </span>
              ) : (
                <span onClick={handleRefresh} className={'menuOption'}>
                  <Refresh htmlColor={'#000'} />
                </span>
              )}
            </div>
          )}
        </div>
      </StyledRootDiv>
      <TroubleshootModal
        open={troubleshootingModalOpen}
        onClose={() => setTroubleshootingModalOpen(false)}
      />
    </>
  );
};

const StyledRootDiv = styled('div')(({ theme: { palette } }) => ({
  height: '40px',
  backgroundColor: palette.status.red,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  '&.restored': {
    backgroundColor: palette.status.green,
  },
  '.menuOptions': {
    display: 'flex',
    width: '100%',
    color: palette.black,
    margin: '0 24px',
  },
  '.menuTitle': {
    display: 'flex',
    alignItems: 'center',
  },
  '.menuButtons': {
    display: 'flex',
    marginLeft: 'auto',
  },
  '.menuOption': {
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    marginLeft: '16px',
    '&.online': {
      marginLeft: 'auto',
    },
  },
  '.menuOptionText': {
    color: palette.black,
  },
  '.timer': {
    backgroundColor: palette.black,
    borderRadius: '10px',
    fontSize: '12px',
    lineHeight: '16px',
    padding: '0 8px',
    opacity: 0,
    transition: 'opacity 0.5s',
    width: '49px',
    '&.online': {
      color: palette.status.green,
    },
    '&.refresh': {
      color: palette.status.red,
    },
    '&.active': {
      opacity: 1,
    },
  },
}));

export default observer(SystemOfflineBanner);
